import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import BuyCreditsModal from './BuyCreditsModal';

const Navbar = () => {
  const { user, loading, isAdmin, credits } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const navLinks = [
    { label: 'Home', path: '/' },
    { label: 'Templates', path: '/templates' },
    { label: 'Pricing', path: '/pricing' },
    ...(user ? [{ label: 'Dashboard', path: '/dashboard' }] : []),
    ...(isAdmin ? [{ label: 'Admin', path: '/admin/dashboard' }] : []),
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <div className="w-8 h-8 bg-gray-900 rounded-lg flex items-center justify-center">
              <i className="ri-file-text-line text-white text-sm"></i>
            </div>
            <span className="font-bold text-gray-900 text-lg tracking-tight">WorkflowTools</span>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isActive(link.path)
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-3">
            {!loading && user ? (
              <>
                {/* Credits Counter */}
                <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-coin-line text-amber-600 text-xs"></i>
                  </div>
                  <span className="text-amber-700 text-sm font-semibold whitespace-nowrap">
                    {credits.toLocaleString()} credits
                  </span>
                </div>

                {/* Buy Credits Button */}
                <button
                  onClick={() => setShowBuyModal(true)}
                  className="bg-gray-900 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Buy Credits
                </button>

                {/* User Menu */}
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 cursor-pointer p-1 rounded-lg hover:bg-gray-50"
                  >
                    <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                      <span className="text-gray-700 text-sm font-semibold">
                        {(user.full_name || user.email || 'U').charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-down-s-line text-gray-500 text-sm"></i>
                    </div>
                  </button>
                  {userMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-gray-100 rounded-xl shadow-lg overflow-hidden">
                      <div className="px-4 py-3 border-b border-gray-50">
                        <p className="text-sm font-semibold text-gray-900 truncate">{user.full_name || 'User'}</p>
                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                      </div>
                      <Link
                        to="/dashboard"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-dashboard-line text-gray-400 text-xs"></i>
                        </div>
                        Dashboard
                      </Link>
                      <button
                        onClick={() => { setShowBuyModal(true); setUserMenuOpen(false); }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-coin-line text-gray-400 text-xs"></i>
                        </div>
                        Buy Credits
                      </button>
                      <div className="border-t border-gray-50 mt-1">
                        <button
                          onClick={handleSignOut}
                          className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 cursor-pointer"
                        >
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-logout-box-line text-red-400 text-xs"></i>
                          </div>
                          Sign out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : !loading ? (
              <>
                <Link
                  to="/login"
                  className="text-gray-600 text-sm font-medium hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Sign in
                </Link>
                <Link
                  to="/signup"
                  className="bg-gray-900 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Get Started
                </Link>
              </>
            ) : null}
          </div>

          {/* Mobile toggle */}
          <button
            className="md:hidden p-2 cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-gray-700`}></i>
            </div>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden border-t border-gray-100 bg-white px-6 py-4 space-y-1">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileOpen(false)}
                className="block px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer"
              >
                {link.label}
              </Link>
            ))}
            {user ? (
              <div className="pt-3 border-t border-gray-100 space-y-2">
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 rounded-lg">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-coin-line text-amber-600 text-xs"></i>
                  </div>
                  <span className="text-amber-700 text-sm font-semibold">{credits.toLocaleString()} credits</span>
                </div>
                <button
                  onClick={() => { setShowBuyModal(true); setMobileOpen(false); }}
                  className="w-full bg-gray-900 text-white text-sm font-medium px-4 py-2.5 rounded-lg cursor-pointer"
                >
                  Buy Credits
                </button>
                <button
                  onClick={handleSignOut}
                  className="w-full text-red-500 text-sm font-medium px-4 py-2.5 rounded-lg border border-red-200 cursor-pointer"
                >
                  Sign out
                </button>
              </div>
            ) : (
              <div className="pt-3 border-t border-gray-100 flex gap-2">
                <Link to="/login" onClick={() => setMobileOpen(false)} className="flex-1 text-center bg-gray-100 text-gray-700 text-sm font-medium py-2.5 rounded-lg cursor-pointer">Sign in</Link>
                <Link to="/signup" onClick={() => setMobileOpen(false)} className="flex-1 text-center bg-gray-900 text-white text-sm font-medium py-2.5 rounded-lg cursor-pointer">Get Started</Link>
              </div>
            )}
          </div>
        )}
      </nav>

      {showBuyModal && <BuyCreditsModal onClose={() => setShowBuyModal(false)} />}
    </>
  );
};

export default Navbar;
